import type { ReactNode } from 'react';
import React from 'react';

import type {
  ComponentRenderProps,
  GridProps,
} from '../../../types/dynamic_views';

const GAP_CLASSES: Record<string, string> = {
  none: 'gap-0',
  sm: 'gap-2',
  md: 'gap-4',
  lg: 'gap-6',
};

const RESPONSIVE_GRID_COLS: Record<number, string> = {
  1: 'grid-cols-1',
  2: 'grid-cols-1 md:grid-cols-2',
  3: 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3',
  4: 'grid-cols-1 md:grid-cols-2 lg:grid-cols-4',
  5: 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5',
  6: 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6',
  12: 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-6',
};

const FIXED_GRID_COLS: Record<number, string> = {
  1: 'grid-cols-1',
  2: 'grid-cols-2',
  3: 'grid-cols-3',
  4: 'grid-cols-4',
  5: 'grid-cols-5',
  6: 'grid-cols-6',
  7: 'grid-cols-7',
  8: 'grid-cols-8',
  9: 'grid-cols-9',
  10: 'grid-cols-10',
  11: 'grid-cols-11',
  12: 'grid-cols-12',
};

const getGridCols = (cols: number, responsive: boolean): string => {
  if (responsive) {
    return RESPONSIVE_GRID_COLS[cols] || RESPONSIVE_GRID_COLS[2];
  }
  return FIXED_GRID_COLS[cols] || 'grid-cols-2';
};

interface GridComponentProps extends ComponentRenderProps {
  children: ReactNode;
}

/**
 * Layout container — never interactive. See SectionComponent for rationale.
 */
export const GridComponent: React.FC<GridComponentProps> = ({
  component,
  className = '',
  children,
}) => {
  const props = component.props as GridProps;
  const { columns = 2, gap = 'md', responsive = true } = props;

  const allClasses = [
    'grid',
    getGridCols(columns, responsive),
    GAP_CLASSES[gap] || GAP_CLASSES.md,
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div
      className={allClasses}
      data-component-id={component.id}
      data-columns={columns}
      data-responsive={responsive}
    >
      {children}
    </div>
  );
};

export default GridComponent;
